import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  ButtonGroup,
  Button,
  TextField,
  Stack,
  Divider,
  CircularProgress,
  Paper,
  Backdrop,
} from '@mui/material';
import { 
  CalendarToday, 
  AttachMoney, 
  People, 
  EventAvailable, 
  TrendingUp,
  DateRange,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

const Dashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | 'last7days' | 'specific' | 'custom'>('today');
  const [specificDate, setSpecificDate] = useState<Date | null>(new Date());
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(true);
  interface ServiceItem {
    name: string;
    count: number;
  }
  
  interface StaffItem {
    name: string;
    count: number;
  }
  
  interface AppointmentItem {
    client: string;
    service: string;
    time: string;
    status: string;
  }
  
  const [stats, setStats] = useState({
    revenue: 0,
    paidRevenue: 0,
    expectedRevenue: 0,
    appointments: 0,
    clients: 0,
    products: 0,
    topServices: [] as ServiceItem[],
    topStaff: [] as StaffItem[],
    recentAppointments: [] as AppointmentItem[],
  });

  useEffect(() => {
    fetchDashboardData();
    // Log the current date range for debugging
    console.log('Date range changed:', dateRange, { specificDate, startDate, endDate });
  }, [dateRange, specificDate, startDate, endDate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      let start, end;
      
      switch (dateRange) {
        case 'today':
          start = startOfDay(new Date());
          end = endOfDay(new Date());
          break;
        case 'yesterday':
          start = startOfDay(subDays(new Date(), 1));
          end = endOfDay(subDays(new Date(), 1));
          break;
        case 'last7days':
          start = startOfDay(subDays(new Date(), 6));
          end = endOfDay(new Date());
          break;
        case 'specific':
          if (specificDate) {
            start = startOfDay(specificDate);
            end = endOfDay(specificDate);
          } else {
            start = startOfDay(new Date());
            end = endOfDay(new Date());
          }
          break;
        case 'custom':
          if (startDate && endDate) {
            start = startOfDay(startDate);
            end = endOfDay(endDate);
          } else {
            start = startOfDay(subDays(new Date(), 7));
            end = endOfDay(new Date());
          }
          break;
        default:
          start = startOfDay(new Date());
          end = endOfDay(new Date());
      }

      // Format dates for API in a simple format that PostgreSQL can understand
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');

      try {
        // Use simple date strings directly in the query
        const response = await fetch(`https://salon.rockyroadsolutions.com/api/dashboard?start=${startStr}&end=${endStr}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats({
            revenue: data.revenue || 0,
            paidRevenue: data.paidRevenue || 0,
            expectedRevenue: data.expectedRevenue || 0,
            appointments: data.appointments || 0,
            clients: data.clients || 0,
            products: data.products || 0,
            topServices: data.topServices || [],
            topStaff: data.topStaff || [],
            recentAppointments: data.recentAppointments || [],
          });
          setLoading(false);
          return;
        } else {
          const errorText = await response.text();
          console.error('Failed to fetch dashboard data:', errorText);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error fetching dashboard data from API:', error);
        setLoading(false);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const getDateRangeText = () => {
    switch (dateRange) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'last7days':
        return 'Last 7 Days';
      case 'specific':
        return specificDate ? format(specificDate, 'MMM dd, yyyy') : 'Specific Date';
      case 'custom':
        return startDate && endDate 
          ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
          : 'Custom Range';
      default:
        return 'Today';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Dashboard
        </Typography>
        <Box>
          <ButtonGroup variant="outlined" size="small" sx={{ mr: 2 }}>
            <Button 
              onClick={() => setDateRange('today')}
              variant={dateRange === 'today' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'today' ? 'primary.main' : 'transparent',
                color: dateRange === 'today' ? 'white' : 'inherit',
              }}
            >
              Today
            </Button>
            <Button 
              onClick={() => setDateRange('yesterday')}
              variant={dateRange === 'yesterday' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'yesterday' ? 'primary.main' : 'transparent',
                color: dateRange === 'yesterday' ? 'white' : 'inherit',
              }}
            >
              Yesterday
            </Button>
            <Button 
              onClick={() => setDateRange('last7days')}
              variant={dateRange === 'last7days' ? 'contained' : 'outlined'}
              sx={{ 
                bgcolor: dateRange === 'last7days' ? 'primary.main' : 'transparent',
                color: dateRange === 'last7days' ? 'white' : 'inherit',
              }}
            >
              Last 7 Days
            </Button>
          </ButtonGroup>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {dateRange === 'specific' ? (
              <DatePicker
                label="Specific Date"
                value={specificDate}
                onChange={(newValue) => setSpecificDate(newValue)}
                slotProps={{ textField: { size: 'small', sx: { width: 150, mt: 2 } } }}
              />
            ) : dateRange === 'custom' ? (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
              </Stack>
            ) : (
              <ButtonGroup variant="outlined" size="small">
                <Button 
                  onClick={() => setDateRange('specific')}
                  startIcon={<CalendarToday />}
                >
                  Specific Date
                </Button>
                <Button 
                  onClick={() => setDateRange('custom')}
                  startIcon={<DateRange />}
                >
                  Custom Range
                </Button>
              </ButtonGroup>
            )}
          </LocalizationProvider>
        </Box>
      </Box>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        Showing data for: <strong>{getDateRangeText()}</strong>
      </Typography>

      {loading ? (
        <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AttachMoney sx={{ color: '#E91E63', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>Revenue</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#E91E63' }}>
                    ${stats.revenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <Box component="span" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                      Paid: ${stats.paidRevenue.toLocaleString()}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary', mx: 1 }}>|</Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      Expected: ${stats.expectedRevenue.toLocaleString()}
                    </Box>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventAvailable sx={{ color: '#9C27B0', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>Appointments</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#9C27B0' }}>
                    {stats.appointments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <People sx={{ color: '#2196F3', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>New Clients</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2196F3' }}>
                    {stats.clients}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUp sx={{ color: '#4CAF50', mr: 1 }} />
                    <Typography variant="h6" fontWeight={600}>Products Sold</Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#4CAF50' }}>
                    {stats.products}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Services & Staff */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Top Services
                  </Typography>
                  {stats.topServices.map((service, index) => (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body1">{service.name}</Typography>
                        <Typography variant="body1" fontWeight={600}>{service.count}</Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 8, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Box 
                          sx={{ 
                            height: '100%', 
                            width: `${(service.count / 20) * 100}%`, 
                            bgcolor: '#E91E63',
                            borderRadius: 1
                          }} 
                        />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Top Staff
                  </Typography>
                  {stats.topStaff.map((staff, index) => (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body1">{staff.name}</Typography>
                        <Typography variant="body1" fontWeight={600}>{staff.count}</Typography>
                      </Box>
                      <Box sx={{ width: '100%', height: 8, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Box 
                          sx={{ 
                            height: '100%', 
                            width: `${(staff.count / 15) * 100}%`, 
                            bgcolor: '#9C27B0',
                            borderRadius: 1
                          }} 
                        />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Appointments */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Recent Appointments
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>Client</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>Service</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentAppointments.map((appointment, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '12px 16px' }}>{appointment.client}</td>
                        <td style={{ padding: '12px 16px' }}>{appointment.service}</td>
                        <td style={{ padding: '12px 16px' }}>{appointment.time}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <Box 
                            component="span" 
                            sx={{ 
                              px: 1.5, 
                              py: 0.5, 
                              borderRadius: 1, 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: 
                                appointment.status === 'Completed' ? '#e8f5e9' : 
                                appointment.status === 'In Progress' ? '#fff8e1' : '#e3f2fd',
                              color: 
                                appointment.status === 'Completed' ? '#2e7d32' : 
                                appointment.status === 'In Progress' ? '#f57c00' : '#1976d2',
                            }}
                          >
                            {appointment.status}
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
